import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin, Loader2, X } from "lucide-react";
import { api } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import Seo, { itemListJsonLd, breadcrumbJsonLd } from "../components/Seo";
import { trackMeta } from "../lib/metaPixel";

const PAGE_SIZE = 12;

const AMENITY_OPTIONS = [
    "Parking", "Lift", "Power Backup", "Security", "Gym",
    "Swimming Pool", "Garden", "Club House", "CCTV", "Water Supply",
    "Gas Pipeline", "Wi-Fi",
];
const FURNISHING_OPTIONS = [
    { v: "", l: "Any" }, { v: "furnished", l: "Furnished" },
    { v: "semi_furnished", l: "Semi" }, { v: "unfurnished", l: "Unfurn." },
];
const FACING_OPTIONS = [
    { v: "", l: "Any" }, { v: "east", l: "East" }, { v: "west", l: "West" },
    { v: "north", l: "North" }, { v: "south", l: "South" },
    { v: "north_east", l: "N-East" }, { v: "north_west", l: "N-West" },
    { v: "south_east", l: "S-East" }, { v: "south_west", l: "S-West" },
];
const POSSESSION_OPTIONS = [
    { v: "", l: "Any" }, { v: "ready_to_move", l: "Ready" },
    { v: "under_construction", l: "Under-const." },
];
const AGE_OPTIONS = [
    { v: "", l: "Any" }, { v: "1", l: "<1 yr" }, { v: "5", l: "<5 yr" },
    { v: "10", l: "<10 yr" }, { v: "20", l: "<20 yr" },
];

export default function Properties() {
    const [params, setParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const sentinelRef = useRef(null);
    const [filters, setFilters] = useState({
        city: params.get("city") || "",
        locality: params.get("locality") || "",
        listing_type: params.get("listing") || "",
        property_type: params.get("type") || "",
        min_price: "",
        max_price: "",
        min_area: "",
        max_area: "",
        bhk: "",
        furnishing: "",
        facing: "",
        possession_status: "",
        max_age: "",
        verified: false,
        amenities: [], // array of strings, joined at query time
        search: "",
    });
    const [localitySuggestions, setLocalitySuggestions] = useState([]);
    const [showLocalityList, setShowLocalityList] = useState(false);

    const buildQuery = useCallback((extra = {}) => {
        const q = { limit: PAGE_SIZE, ...extra };
        Object.entries(filters).forEach(([k, v]) => {
            if (v === "" || v == null) return;
            if (k === "amenities") {
                if (Array.isArray(v) && v.length > 0) q.amenities = v.join(",");
                return;
            }
            if (k === "verified") {
                if (v) q.verified = true;
                return;
            }
            q[k] = v;
        });
        return q;
    }, [filters]);

    const loadFirst = useCallback(async () => {
        setLoading(true);
        setSkip(0);
        try {
            const { data } = await api.get("/properties", { params: buildQuery({ skip: 0 }) });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setSkip((data.items || []).length);
        } finally {
            setLoading(false);
        }
    }, [buildQuery]);

    const loadMore = useCallback(async () => {
        if (loading || loadingMore) return;
        if (items.length >= total) return;
        setLoadingMore(true);
        try {
            const { data } = await api.get("/properties", { params: buildQuery({ skip: items.length }) });
            const next = data.items || [];
            setItems((prev) => [...prev, ...next]);
            setSkip(items.length + next.length);
        } finally {
            setLoadingMore(false);
        }
    }, [items.length, total, loading, loadingMore, buildQuery]);

    // Initial load
    useEffect(() => { loadFirst(); }, []);

    // Infinite scroll — observe sentinel
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el) return;
        const io = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) loadMore();
        }, { rootMargin: "600px 0px" });
        io.observe(el);
        return () => io.disconnect();
    }, [loadMore]);

    // Lock body scroll while mobile filter sheet is open
    useEffect(() => {
        if (!showMobileFilters) return;
        const original = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = original; };
    }, [showMobileFilters]);

    // Locality autocomplete — debounced
    useEffect(() => {
        const q = (filters.locality || "").trim();
        if (q.length < 1) {
            setLocalitySuggestions([]);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const { data } = await api.get("/properties/localities", {
                    params: { q, city: filters.city || undefined, limit: 8 },
                });
                setLocalitySuggestions(data.items || []);
            } catch {
                setLocalitySuggestions([]);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [filters.locality, filters.city]);

    const onApply = (e) => {
        e?.preventDefault();
        const sp = new URLSearchParams();
        if (filters.city) sp.set("city", filters.city);
        if (filters.locality) sp.set("locality", filters.locality);
        if (filters.listing_type) sp.set("listing", filters.listing_type);
        if (filters.property_type) sp.set("type", filters.property_type);
        setParams(sp);
        setShowMobileFilters(false);
        setShowLocalityList(false);
        // Fire Meta Search event
        try {
            trackMeta("Search", {
                search_string: filters.search || filters.locality || filters.city,
                content_category: filters.property_type || "property",
                city: filters.city,
                locality: filters.locality,
            });
        } catch { /* silent */ }
        loadFirst();
    };

    const resetFilters = () => {
        setFilters({
            city: "", locality: "", listing_type: "", property_type: "",
            min_price: "", max_price: "", min_area: "", max_area: "",
            bhk: "", furnishing: "", facing: "", possession_status: "",
            max_age: "", verified: false, amenities: [], search: "",
        });
        setParams(new URLSearchParams());
        setLocalitySuggestions([]);
    };

    const toggleAmenity = (a) => {
        setFilters((f) => {
            const has = f.amenities.includes(a);
            return { ...f, amenities: has ? f.amenities.filter((x) => x !== a) : [...f.amenities, a] };
        });
    };

    // Build city/area-aware SEO based on active filters
    const seo = useMemo(() => {
        const city = filters.city || params.get("city") || "";
        const lt = filters.listing_type || params.get("listing") || "";
        const pt = filters.property_type || params.get("type") || "";
        const labelType = pt === "plot" ? "Plots" : pt === "commercial" ? "Commercial Properties" : pt === "farmland" ? "Farmland" : pt === "residential" ? "Homes & Flats" : "Properties";
        const labelAction = lt === "rent" ? "for Rent" : lt === "sale" ? "for Sale" : lt === "pg" ? "PG / Hostels" : "for Sale & Rent";
        const place = city ? ` in ${city}` : " in Nagpur, Maharashtra";
        const title = `${labelType} ${labelAction}${place}`;
        const desc = `Browse verified ${labelType.toLowerCase()} ${labelAction.toLowerCase()}${place}. RERA-verified brokers, RealEstateListing-rich search across Nagpur, Wardha Road, MIHAN, Mohgaon, Beltarodi, Jamtha & Maharashtra.`;
        const path = `/properties${params.toString() ? "?" + params.toString() : ""}`;
        const keywords = [
            "plots in Nagpur", "property in Nagpur", "buy plot in Nagpur", "residential plots in Nagpur",
            "commercial property in Nagpur", "property dealers in Nagpur", "real estate company in Nagpur",
            "property in Maharashtra", "plots in Maharashtra", "auction properties in Nagpur",
            "resale property in Nagpur", "investment property in Nagpur", "gated community plots Nagpur",
            "property near Wardha Road", "plots near MIHAN", "plots near Samruddhi Mahamarg",
        ].join(", ");
        return { title, desc, path, keywords };
    }, [filters, params]);

    const FiltersForm = (
        <form onSubmit={onApply} className="rounded-2xl bg-navy-800 border border-white/5 p-5 space-y-4">
            <div className="flex items-center gap-2 text-gold-500">
                <SlidersHorizontal className="w-4 h-4" />
                <span className="label-overline">Filters</span>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Search</label>
                <div className="relative">
                    <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        data-testid="filter-search"
                        type="text"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        placeholder="Title, locality"
                        className="w-full bg-navy-900 border border-white/10 rounded-md pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/40 focus:border-gold-500/60 focus:outline-none"
                    />
                </div>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Listing</label>
                <div className="grid grid-cols-3 gap-1 bg-navy-900 rounded-md p-1 border border-white/10">
                    {[{v:"",l:"Any"},{v:"sale",l:"Buy"},{v:"rent",l:"Rent"}].map((o) => (
                        <button
                            key={o.v}
                            type="button"
                            data-testid={`filter-listing-${o.v||"any"}`}
                            onClick={() => setFilters({ ...filters, listing_type: o.v })}
                            className={`px-2 py-1.5 text-xs font-semibold rounded ${
                                filters.listing_type === o.v ? "bg-gold-500 text-navy-900" : "text-white/70"
                            }`}
                        >{o.l}</button>
                    ))}
                </div>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Property type</label>
                <select
                    data-testid="filter-type"
                    value={filters.property_type}
                    onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
                    className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                >
                    <option value="">Any</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="plot">Plot</option>
                    <option value="farmland">Farmland</option>
                </select>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">City</label>
                <input
                    data-testid="filter-city"
                    type="text"
                    value={filters.city}
                    onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                    placeholder="e.g. Nagpur"
                    className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40"
                />
            </div>

            <div className="relative">
                <label className="text-xs text-white/60 mb-1 block">Locality</label>
                <input
                    data-testid="filter-locality"
                    type="text"
                    value={filters.locality}
                    onChange={(e) => { setFilters({ ...filters, locality: e.target.value }); setShowLocalityList(true); }}
                    onFocus={() => setShowLocalityList(true)}
                    onBlur={() => setTimeout(() => setShowLocalityList(false), 150)}
                    placeholder="e.g. Wardha Road, MIHAN"
                    className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-white/40"
                    autoComplete="off"
                />
                {showLocalityList && localitySuggestions.length > 0 && (
                    <div
                        className="absolute z-30 mt-1 w-full max-h-60 overflow-y-auto rounded-md bg-white border border-navy-500/15 shadow-xl"
                        data-testid="locality-suggestions"
                    >
                        {localitySuggestions.map((s) => (
                            <button
                                key={`${s.locality}-${s.city}`}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); setFilters((f) => ({ ...f, locality: s.locality, city: f.city || s.city })); setShowLocalityList(false); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gold-500/10 flex items-center justify-between border-b border-navy-500/5 last:border-0"
                            >
                                <div>
                                    <div className="text-navy-500 font-semibold">{s.locality}</div>
                                    <div className="text-xs text-navy-500/60">{s.city}</div>
                                </div>
                                <span className="text-[10px] text-gold-700 font-bold px-2 py-0.5 rounded-full bg-gold-500/10">{s.count}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="text-xs text-white/60 mb-1 block">Min ₹</label>
                    <input
                        type="number"
                        data-testid="filter-min-price"
                        value={filters.min_price}
                        onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                        className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    />
                </div>
                <div>
                    <label className="text-xs text-white/60 mb-1 block">Max ₹</label>
                    <input
                        type="number"
                        data-testid="filter-max-price"
                        value={filters.max_price}
                        onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                        className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    />
                </div>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">BHK</label>
                <div className="grid grid-cols-5 gap-1">
                    {["", "1", "2", "3", "4"].map((b) => (
                        <button
                            key={b || "any"}
                            type="button"
                            onClick={() => setFilters({ ...filters, bhk: b })}
                            className={`py-1.5 text-xs rounded font-semibold border ${
                                filters.bhk === b ? "bg-gold-500 text-navy-900 border-gold-500" : "border-white/10 text-white/70"
                            }`}
                        >{b ? `${b}+` : "Any"}</button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="text-xs text-white/60 mb-1 block">Min sqft</label>
                    <input
                        type="number" data-testid="filter-min-area"
                        value={filters.min_area}
                        onChange={(e) => setFilters({ ...filters, min_area: e.target.value })}
                        className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    />
                </div>
                <div>
                    <label className="text-xs text-white/60 mb-1 block">Max sqft</label>
                    <input
                        type="number" data-testid="filter-max-area"
                        value={filters.max_area}
                        onChange={(e) => setFilters({ ...filters, max_area: e.target.value })}
                        className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                    />
                </div>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Furnishing</label>
                <div className="grid grid-cols-4 gap-1">
                    {FURNISHING_OPTIONS.map((o) => (
                        <button
                            key={o.v || "any"}
                            type="button"
                            data-testid={`filter-furnishing-${o.v || "any"}`}
                            onClick={() => setFilters({ ...filters, furnishing: o.v })}
                            className={`py-1.5 text-[11px] rounded font-semibold border ${
                                filters.furnishing === o.v ? "bg-gold-500 text-navy-900 border-gold-500" : "border-white/10 text-white/70"
                            }`}
                        >{o.l}</button>
                    ))}
                </div>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Possession</label>
                <div className="grid grid-cols-3 gap-1">
                    {POSSESSION_OPTIONS.map((o) => (
                        <button
                            key={o.v || "any"}
                            type="button"
                            data-testid={`filter-possession-${o.v || "any"}`}
                            onClick={() => setFilters({ ...filters, possession_status: o.v })}
                            className={`py-1.5 text-[11px] rounded font-semibold border ${
                                filters.possession_status === o.v ? "bg-gold-500 text-navy-900 border-gold-500" : "border-white/10 text-white/70"
                            }`}
                        >{o.l}</button>
                    ))}
                </div>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Facing</label>
                <select
                    data-testid="filter-facing"
                    value={filters.facing}
                    onChange={(e) => setFilters({ ...filters, facing: e.target.value })}
                    className="w-full bg-navy-900 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
                >
                    {FACING_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Property Age</label>
                <div className="grid grid-cols-5 gap-1">
                    {AGE_OPTIONS.map((o) => (
                        <button
                            key={o.v || "any"}
                            type="button"
                            data-testid={`filter-age-${o.v || "any"}`}
                            onClick={() => setFilters({ ...filters, max_age: o.v })}
                            className={`py-1.5 text-[11px] rounded font-semibold border ${
                                filters.max_age === o.v ? "bg-gold-500 text-navy-900 border-gold-500" : "border-white/10 text-white/70"
                            }`}
                        >{o.l}</button>
                    ))}
                </div>
            </div>

            <div>
                <label className="text-xs text-white/60 mb-1 block">Amenities</label>
                <div className="flex flex-wrap gap-1.5">
                    {AMENITY_OPTIONS.map((a) => {
                        const on = filters.amenities.includes(a);
                        return (
                            <button
                                key={a}
                                type="button"
                                data-testid={`filter-amenity-${a.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => toggleAmenity(a)}
                                className={`px-2.5 py-1 text-[11px] rounded-full font-semibold border transition-colors ${
                                    on ? "bg-gold-500 text-navy-900 border-gold-500" : "border-white/15 text-white/70 hover:border-gold-500/50"
                                }`}
                            >{a}</button>
                        );
                    })}
                </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                    type="checkbox"
                    data-testid="filter-verified"
                    checked={filters.verified}
                    onChange={(e) => setFilters({ ...filters, verified: e.target.checked })}
                    className="accent-gold-500"
                />
                <span className="text-xs text-white/80 font-semibold inline-flex items-center gap-1">
                    Verified listings only <span className="text-gold-500">🛡</span>
                </span>
            </label>

            <div className="flex items-center gap-2 pt-1">
                <button
                    type="submit"
                    data-testid="filter-apply"
                    className="flex-1 px-4 py-2.5 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100"
                >Apply Filters</button>
                <button
                    type="button"
                    onClick={resetFilters}
                    data-testid="filter-reset"
                    className="px-3 py-2.5 rounded-md border border-white/15 text-white/70 text-xs font-semibold hover:border-white/30"
                >Reset</button>
            </div>
        </form>
    );

    return (
        <div className="min-h-screen bg-navy-900">
            <Seo
                title={seo.title}
                description={seo.desc}
                path={seo.path}
                keywords={seo.keywords}
                jsonLd={[
                    itemListJsonLd(items.slice(0, 20), { name: seo.title }),
                    breadcrumbJsonLd([
                        { name: "Home", path: "/" },
                        { name: "Properties", path: "/properties" },
                        ...(filters.city ? [{ name: filters.city, path: `/properties?city=${encodeURIComponent(filters.city)}` }] : []),
                    ]),
                ]}
            />
            <div className="bg-navy-950 border-b border-white/5">
                <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-8 sm:py-12">
                    <div className="label-overline mb-2">Marketplace</div>
                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white">All Properties</h1>
                    <p className="text-white/65 text-sm sm:text-base mt-2 max-w-2xl">Browse verified listings across India. Use filters to narrow down to your perfect home, plot, or investment.</p>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-6 sm:py-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                {/* FILTERS — desktop sidebar */}
                <aside className="hidden lg:block lg:col-span-3 space-y-5" data-testid="properties-filters">
                    <div className="sticky top-20">{FiltersForm}</div>
                </aside>

                {/* Mobile filter trigger */}
                <div className="lg:hidden flex items-center justify-end">
                    <button
                        type="button"
                        onClick={() => setShowMobileFilters(true)}
                        data-testid="mobile-filter-toggle"
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gold-500 text-navy-900 text-xs font-bold border border-gold-500/50 shadow-sm"
                    >
                        <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
                    </button>
                </div>

                {/* Mobile filters — full-screen sheet (Portaled to body to escape transformed ancestors) */}
                {showMobileFilters && createPortal(
                    <div className="lg:hidden fixed inset-0 z-[100] flex flex-col" data-testid="mobile-filters-overlay">
                        <button
                            type="button"
                            aria-label="Close filters"
                            onClick={() => setShowMobileFilters(false)}
                            className="flex-1 bg-black/55 backdrop-blur-sm"
                        />
                        <div
                            className="max-h-[86vh] overflow-y-auto rounded-t-3xl bg-white border-t border-navy-500/10 p-5 pb-8 shadow-2xl"
                            data-testid="mobile-filters-sheet"
                        >
                            <div className="w-10 h-1 rounded-full bg-navy-500/20 mx-auto mb-4" />
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-heading font-bold text-lg text-navy-500">Filter properties</h3>
                                <button onClick={() => setShowMobileFilters(false)} className="w-8 h-8 rounded-full bg-navy-500/10 flex items-center justify-center" data-testid="mobile-filters-close">
                                    <X className="w-4 h-4 text-navy-500" />
                                </button>
                            </div>
                            {FiltersForm}
                        </div>
                    </div>,
                    document.body
                )}

                {/* RESULTS */}
                <section className="lg:col-span-9">
                    <div className="hidden lg:flex items-center justify-between mb-6">
                        <div className="text-white/70 text-sm">
                            {loading ? "Searching…" : <><span className="text-gold-500 font-bold">{total}</span> properties found</>}
                        </div>
                    </div>

                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-testid="properties-loading">
                            {[1,2,3,4].map((i) => <div key={i} className="h-72 rounded-2xl shimmer" />)}
                        </div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-24 rounded-2xl border border-white/5 bg-navy-800">
                            <MapPin className="w-12 h-12 text-gold-500/60 mx-auto mb-4" />
                            <h3 className="font-heading text-2xl font-bold text-white mb-2">No properties found</h3>
                            <p className="text-white/60 text-sm">Try adjusting filters or searching different cities.</p>
                        </div>
                    ) : (
                        <>
                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mobile-listing"
                                data-testid="properties-grid"
                            >
                                {items.map((p, idx) => (
                                    <div key={p.id} className={`card-enter card-enter-${Math.min(idx % 12, 11)}`}>
                                        <PropertyCard property={p} />
                                    </div>
                                ))}
                            </div>

                            {/* Infinite-scroll sentinel + status */}
                            <div ref={sentinelRef} className="h-1" aria-hidden="true" />
                            <div className="mt-6 sm:mt-8 flex flex-col items-center justify-center gap-3">
                                {loadingMore && (
                                    <div className="inline-flex items-center gap-2 text-white/70 text-sm" data-testid="loading-more">
                                        <Loader2 className="w-4 h-4 animate-spin text-gold-500" />
                                        Loading more properties…
                                    </div>
                                )}
                                {!loadingMore && items.length < total && (
                                    <button
                                        type="button"
                                        onClick={loadMore}
                                        data-testid="load-more-btn"
                                        className="gold-pulse px-6 py-2.5 rounded-full bg-gold-500 text-navy-900 text-sm font-bold border border-gold-500/40 shadow-sm hover:bg-gold-100 transition-colors"
                                    >
                                        Load more ({total - items.length} left)
                                    </button>
                                )}
                                {!loadingMore && items.length >= total && total > PAGE_SIZE && (
                                    <div className="text-white/50 text-xs uppercase tracking-[0.2em]" data-testid="end-of-list">
                                        · You&apos;ve reached the end ·
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
